<template>
  <form
    v-on:submit.prevent="onSubmit"
    autocomplete="off"
    novalidate="true"
    class="cabinet-creation__form">
    <input-text
      placeholder="Например, Кресло ортодонта"
      title="Название"
      v-bind:violations="nameInputErrors"
      v-letter-capital
      v-model="cabinetName" />
    <textarea-length-limitation
      placeholder="Кроме хирургии"
      title="Комментарий"
      v-bind:max-length="$options.MAX_COMMENT_LENGTH"
      v-bind:violations="commentInputErrors"
      v-letter-capital
      v-model="comment" />
    <div class="cabinet-creation__buttons">
      <button
        class="cabinet-creation__button-cancel"
        type="button"
        v-on:click.prevent="close">Отмена</button>
      <button
        class="cabinet-creation__button-save"
        type="submit">
        {{ this.isEdit ? "Изменить" : "Создать" }}
      </button>
    </div>
  </form>
</template>

<script>
import notifierMixin from '@/mixins/notifierMixin';
import { CabinetCreatingRequest, CabinetEditingRequest } from '@/storage/gateways/CabinetGateway';
import formMeaningfulFieldsMixin from '@/mixins/formMeaningfulFieldsMixin';
import InputText from '@/components/InputText.vue';
import TextareaLengthLimitation from '@/components/TextareaLengthLimitation.vue';

/**
 * @description Максимальная длина комментария.
 * @type {string}
 */
const MAX_COMMENT_LENGTH = 100;

/**
 * @description Название события создания кабинета.
 * @type {string}
 */
const NAME_EVENT_CREATE_CABINET = 'submit-creating';

/**
 * @description Название события редактирования кабинета.
 * @type {string}
 */
const NAME_EVENT_EDIT_CABINET = 'submit-editing';

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.inputErrors ? vm.inputErrors[cause] : []
);

/**
 * @description Форма создания/редактирования кабинета.
 * @type {any}
 */
export default {
  MAX_COMMENT_LENGTH,
  components: {
    InputText,
    TextareaLengthLimitation,
  },
  computed: {
    nameInputErrors: resolveInputErrors('name'),
    commentInputErrors: resolveInputErrors('comment'),
    isEdit() {
      return !!this.cabinet;
    },
    /**
     * @description Значимые поля.
     * @type {any}
     */
    meaningfulFields(vm) {
      return {
        cabinetName: vm.cabinetName,
        comment: vm.comment,
      };
    },
  },
  data() {
    return {
      /**
       * @description Название кабинета.
       * @type {string}
       */
      cabinetName: '',
      /**
       * @description Комментарий.
       * @type {string}
       */
      comment: '',
    };
  },
  methods: {
    /**
     * @description Закрыть форму.
     * @returns {void}
     */
    close() {
      this.setDefaultData();
      this.$emit('close');
    },
    loadImage() {
      this.fileInput.click();
    },
    onSubmit() {
      if (this.cabinet) {
        const request = new CabinetEditingRequest(
          this.cabinet.id,
          this.cabinetName,
          this.comment,
        );
        this.$emit(NAME_EVENT_EDIT_CABINET, request);
      } else {
        const request = new CabinetCreatingRequest(
          this.cabinetName,
          this.comment,
        );
        this.$emit(NAME_EVENT_CREATE_CABINET, request);
      }
    },
    async setCabinet(value) {
      if (value) {
        this.cabinetName = value.name;
        this.comment = value.comment || '';
      } else {
        this.setDefaultData();
      }
    },
    setDefaultData() {
      if (this.cabinet) {
        this.cabinetName = this.cabinet.name || '';
        this.comment = this.cabinet.comment || '';
      } else {
        this.cabinetName = '';
        this.comment = '';
      }
    },
  },
  mixins: [notifierMixin, formMeaningfulFieldsMixin],
  name: 'FormEmployeePosition',
  props: {
    /**
     * @description Данные кабинета.
     * @type {any}
     */
    cabinet: { type: Object, default: null },
    /**
     * @description Нарушения ограничений.
     * @type {any}
     */
    inputErrors: { type: Object, required: true },
  },
  watch: {
    cabinet: {
      handler: 'setCabinet',
      immediate: true,
      deep: true,
    },
  },
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";
button {
  outline: none !important;
}
.colorItem:focus,
.cabinet-creation__icon__image-select > button:focus {
  border: 2px solid #c2cfe0;
  border-radius: 50%;
}
.cabinet-creation {
  position: relative;
  &__caption {
    font-weight: 600;
    font-size: 24px;
    line-height: 29px;
  }
  &__exit {
    position: absolute;
    right: -10px;
    top: 0px;
    background-image: url("@/assets/img/exit-black.svg");
    background-position: center;
    background-size: auto;
    background-repeat: no-repeat;
    background-color: #eaeff3;
    padding: 9px;
    border-radius: 2px;
    width: 32px;
    height: 32px;
    cursor: pointer;
    &:hover {
      background-color: transparent;
    }
  }
  &__form {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 20px;
    margin: 30px 0 0 0;
  }
  &__icon {
    &__caption {
      font-weight: 600;
      font-size: 16px;
      line-height: 19px;
      color: #192a3e;
      margin: 0 0 15px 0 !important;
    }
    &__colors {
      flex: 1;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
    }
    &__color {
      min-width: 36px;
      width: 36px;
      height: 36px;
      min-height: 36px;
      border-radius: 50%;
      margin: 0 10px 10px 0;
      cursor: pointer;
      transition: border-width 0.1s;
      &.outline {
        border: 4px solid #c2cfe0;
      }
      &:hover {
        border: 4px solid #c2cfe0;
      }
    }
    &__image-select {
      position: relative;
      width: 86px;
      height: 86px;
      align-self: flex-end;
      margin-right: 40px;
      margin-bottom: 10px;
      &__upload {
        border-radius: 50%;
        width: 100%;
        height: 100%;
        cursor: pointer;
        background: #eaeff3;
        background-image: url("@/assets/img/camera.svg");
        background-repeat: no-repeat;
        background-position: center;
      }
      &__image {
        width: 100%;
        height: 100%;
      }
      &__close::after {
        cursor: pointer;
        font: normal normal bolder 25px/1 "Material Design Icons";
        display: block;
        position: absolute;
        transform: rotate(45deg);
        color: red;
        top: -7px;
        right: -7px;
        content: "\F0415";
      }
      &__image > img {
        border-radius: 50%;
        object-fit: cover;
        width: 100%;
        height: 100%;
      }
    }
    &__one-row {
      display: flex;
      flex-direction: row;
    }
    &__text {
      font-weight: 400;
      font-size: 12px;
      line-height: 16px;
      color: var(--unfocus-color);
    }
  }
  &__buttons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 10px;
    max-width: 438px;
    width: 100%;
    margin: 30px 0 0 auto;
  }
  &__button {
    &-cancel {
      @include buttons.btn-transparent(100%, 56px, 20px);
    }
    &-save {
      @include buttons.btn-blue(100%, 56px, 20px);
    }
  }
}
.validate-error {
  padding: 3px;
  color: red;
  font-size: 0.8rem;
}
</style>
